import React, { useState } from "react";
import { PacmanLoader } from "react-spinners";
import { Row, Col, Button, ListGroup, Alert, Modal } from "react-bootstrap";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createColumnHelper } from "@tanstack/react-table";
import "containers/common/List.css";
import "containers/common/Table.css";

function PropertyClassificationList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  classificationOptions,
  filter,
  user,
}) {
  const dataSet = "property_classifications";
  const tableData = data || [];
  const columnHelper = createColumnHelper();
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  function validateRows(rows) {
    for (const row of rows) {
      if (String(row?.classification_code ?? "") === "") {
        return "Classification cannot be blank. The record has not been saved";
      }
    }

    const seenSortOrders = new Set();

    for (const row of rows) {
      const rawSortOrder = row?.sort_order;
      if (rawSortOrder === null || rawSortOrder === undefined || rawSortOrder === "") {
        continue;
      }

      const normalizedSortOrder = Number(rawSortOrder);
      if (Number.isNaN(normalizedSortOrder)) {
        continue;
      }

      if (seenSortOrders.has(normalizedSortOrder)) {
        return "Duplicate sort orders. The record has not been saved";
      }

      seenSortOrders.add(normalizedSortOrder);
    }

    return null;
  }

  const columns = [
    columnHelper.accessor("property_classification_id", {
      id: "property_classification_id",
      header: "ID",
      size: 60,
    }),
    columnHelper.accessor("classification_code", {
      id: "classification_code",
      header: "Classification",
      cell: EditableCell,
      meta: {
        options: classificationOptions || [],
        saveOnBlurOnly: true,
      },
    }),
    columnHelper.accessor("sort_order", {
      id: "sort_order",
      header: "Sort Order",
      cell: EditableCell,
      size: 85,
    }),
    columnHelper.accessor("delete_property_classification_id", {
      id: "delete_property_classification_id",
      header: "",
      cell: ({ row }) => (
        <Button type="button" size="sm" variant="danger"
          onClick={(e) => handleDeleteRow(row, e)}>
          Delete
        </Button>
      ),
      size: 65,
    }),
  ];

  const tableMeta = {
    updateData: (rowIndex, columnId, value) => {
      updateTableState(dataSet, rowIndex, columnId, value);
    },
    shouldValidateUnchangedOnBlur: ({ column }) => column?.id === "classification_code",
    handleSave: async ({ row, column, value }) => {
      const selectedClassificationOption =
        column?.id === "classification_code"
          ? (classificationOptions || []).find((option) => option.value === value)
          : null;

      const record = {
        ...(data?.[row.index] || {}),
        ...row.original,
        ...(column?.id ? { [column.id]: value } : {}),
        ...(selectedClassificationOption
          ? { classification: selectedClassificationOption.label }
          : {}),
      };

      const updatedRows = [...(data || [])];
      updatedRows[row.index] = record;
      const validationMessage = validateRows(updatedRows);
      if (validationMessage) {
        setErrorMessage(validationMessage);
        setShowAlert(true);
        return false;
      }

      try {
        await saveRecord(dataSet, record);
        console.log("Record saved successfully via parent logic");
      } catch (err) {
        console.error("Save failed:", err);
      }
    },
    handleDelete: ({ row }) => {
      deleteRecord(dataSet, row.original);
    },
  };

  // --- Data Fetching and Update Functions ---
  async function addRecord(event) {
    let localData = [...data];
    //Minimum record to create (all not nulls populated)
    localData.unshift({
      _client_id: `property-classification-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      property_id: filter.propertyid,
      classification_code: "",
      classification: "",
      sort_order: localData.length + 1,
      created_by: user,
      last_edited_by: user,
      new_record: true
    });
    updateTableState(localData, dataSet);
  }

  async function handleDeleteRow(row, event) {
    event.preventDefault();
    console.log("[PropertyClassificationList] Delete clicked", {
      rowIndex: row.index,
      rowOriginal: row.original,
    });

    const localData = [...(data || [])];
    const existingRecord = {
      ...(localData[row.index] || {}),
      ...row.original,
    };

    console.log("[PropertyClassificationList] Resolved delete record", existingRecord);

    if (!existingRecord || Object.keys(existingRecord).length === 0) {
      console.warn("[PropertyClassificationList] Delete aborted: no existing record");
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this record?");
    console.log("[PropertyClassificationList] Delete confirmation", { confirmed });
    if (!confirmed) {
      return;
    }

    if (!existingRecord.property_classification_id && !existingRecord.id) {
      console.log("[PropertyClassificationList] Removing unsaved row locally");
      localData.splice(row.index, 1);
      updateTableState(localData, dataSet);
      return;
    }

    try {
      console.log("[PropertyClassificationList] Calling parent deleteRecord", existingRecord);
      await deleteRecord(dataSet, existingRecord);
      console.log("[PropertyClassificationList] Parent deleteRecord resolved");
    } catch (err) {
      console.error("Delete failed:", err);
    }
  }

  // --- Render Functions ---
  function renderList(data) {
    if (!data) {
      return null
    }
    else {
      const sortedData = [...data].sort((a, b) => Number(a?.sort_order ?? 0) - Number(b?.sort_order ?? 0));
      return [{}].concat(sortedData).map(
        (d, i) =>
          i !== 0
            ?
            <ListGroup.Item key={d.property_classification_id || d._client_id || i} as="a" header={d.classification}>
              {(d.sort_order.toString() + ") ") + d.classification}
            </ListGroup.Item>
            : ""
      );
    }
  }

  function renderComponent() {
    return (
      <div className="componentRendering">
        <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Uh Oh!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger">{errorMessage}</Alert>
          </Modal.Body>
        </Modal>
        {controlType === "table" && (
          <Row className="show-Container">
            <Col className="text-right">
              <Button variant="secondary" size="sm" onClick={addRecord}>Add</Button>
            </Col>
          </Row>
        )}
        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : controlType === "table" ? (
              <GenericTable
                columns={columns}
                data={tableData}
                getRowId={(row, index) => String(row._client_id ?? row.property_classification_id ?? index)}
                section="property-classification"
                pageSize={10}
                meta={tableMeta}
              />
            ) : renderList(data)}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="PropertyClassificationList">
      {isAuthenticated ? renderComponent() : console.log("User not authenticated.")}
    </div>
  );
}

export default PropertyClassificationList;